import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  const calculateRotation = (x, y) => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    const isMobile = window.innerWidth < 768;
    const divider = isMobile ? 100 : 50;
    
    const deltaX = (x - centerX) / divider;
    const deltaY = (centerY - y) / divider;
    
    return { x: deltaY, y: deltaX, z: 0 };
  };

  return (
    <section className="py-16 md:py-32 flex flex-col items-center justify-center text-center relative overflow-hidden min-h-screen w-full">
      {/* Декоративные элементы с ограничением позиции */}
      <div className="absolute top-20 left-0 sm:-left-20 w-60 sm:w-80 h-60 sm:h-80 bg-accent opacity-10 rounded-full filter blur-3xl animate-pulse-glow"></div>
      <div className="absolute bottom-10 right-0 sm:-right-20 w-60 sm:w-72 h-60 sm:h-72 bg-accent-secondary opacity-10 rounded-full filter blur-3xl animate-pulse-glow animation-delay-2000"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 50 }}
          className="space-y-8 md:space-y-10 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center"
          >
            <img 
              src="/logo192.png" 
              alt="Sysrootix Logo" 
              className="w-24 h-24 md:w-32 md:h-32 object-contain" 
            />
          </motion.div>
          
          <motion.div
            style={{ 
              perspective: 1000,
              rotateX: calculateRotation(mousePosition.x, mousePosition.y).x,
              rotateY: calculateRotation(mousePosition.x, mousePosition.y).y
            }}
            className="inline-block"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-4">
              <span className="block bg-cyberpunk text-transparent bg-clip-text pb-2">Sysrootix</span>
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-text-secondary mt-2"
              >
                <span className="text-accent-secondary">{'{'}</span> технологии<span className="text-accent-secondary">{' • '}</span>творчество<span className="text-accent-secondary">{' • '}</span>инновации <span className="text-accent-secondary">{'}'}</span>
              </motion.span>
            </h1>
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="text-lg sm:text-xl md:text-2xl text-text-secondary max-w-2xl mx-auto leading-relaxed"
          >
            Разработчик, маркетолог и создатель полезных технологических решений с уникальным взглядом на мир
          </motion.p>
          
          <motion.div 
            className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(124, 58, 237, 0.5)" }}
              whileTap={{ scale: 0.95 }}
              className="relative group"
            >
              <span className="absolute -inset-0.5 bg-gradient-to-r from-glow-purple to-glow-blue rounded-full blur opacity-60 group-hover:opacity-100 transition duration-500"></span>
              <a
                href="#projects"
                className="relative inline-flex items-center px-5 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-medium rounded-full bg-secondary text-white transition-all duration-300"
              >
                Мои проекты
                <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </a>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(219, 39, 119, 0.5)" }}
              whileTap={{ scale: 0.95 }}
              className="relative group"
            >
              <span className="absolute -inset-0.5 bg-gradient-to-r from-glow-pink to-glow-purple rounded-full blur opacity-60 group-hover:opacity-100 transition duration-500"></span>
              <a
                href="#contact"
                className="relative inline-flex items-center px-5 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-medium rounded-full bg-secondary text-white transition-all duration-300"
              >
                Связаться
                <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero; 